import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AIDraftExpanded,
  AIParkExpanded,
  AIProgressExpanded,
  AIRunButton,
  performerResultHasVisibleBadge,
  performerResultHasExpandedPanel,
  buildCrmProposalKey,
  SHOW_AI_ASSIST_COST,
} from "./shared/AIDraftComponents";
import { useConnectorRunGate } from "./shared/useConnectorRunGate";
import ActionItemCategoryControl from "./shared/ActionItemCategoryControl";
import ActionItemDueDateControl from "./shared/ActionItemDueDateControl";
import SourceMessageAccordion, { InlineSpinner } from "./shared/SourceMessageAccordion";
import {
  buildActionItemCategoryPickerOptions,
  getActionItemCategory,
} from "./actionItemCategories";
import {
  ActionItemArchiveIcon,
  ActionItemDeleteIcon,
  ActionItemRestoreIcon,
} from "./shared/actionItemArchiveIcons";
import RunErrorNotice from "./shared/RunErrorNotice";
import {
  buildActionItemSourceProvenanceParts,
  formatCreatedOnDate,
  formatMeetingLabelForDisplay,
  isCaptureWorkflowItem,
  isManualActionItem,
  parseActionItemCreatedAtMs,
  parseMeetingLabel,
  resolveActionItemMeetingSubject,
  resolveActionItemPageUrl,
  resolveGranolaChatUrl,
  resolveNotionPageUrl,
  resolveActionItemAssigneeLabel,
  resolveActionItemTexts,
  getItemStableId,
  getItemSidebarKey,
  isAiActionSupported,
  resolveReadSourceLabel,
  resolveActionItemSourceFilterId,
  splitActionItemsSourceLabel,
  CAPTURE_WORKFLOW_NAME,
} from "./actionItemHelpers";
import {
  actionItemSourceToneModifier,
  renderActionItemSourceIcon,
  resolveActionItemConnectorSourceType,
} from "./actionItemSourceIcons";

const STATUS_DONE = "done";
const STATUS_IGNORE = "ignore";

/**
 * True when focus left this task's AI panel and landed on a different task row.
 *
 * Progress / park / draft swap unmounts the focused control (Try again, or a
 * progress `<summary>`). Browsers then move focus to the next tabbable, which
 * in the inline list is the next row's checkbox — it looks like the app
 * advanced to the next task right as AI output appears.
 *
 * @example
 * didAiPanelFocusJumpToAnotherRow(panelEl, nextRowCheckbox); // true
 * didAiPanelFocusJumpToAnotherRow(panelEl, panelEl); // false
 */
export const didAiPanelFocusJumpToAnotherRow = (panelEl, activeEl) => {
  if (!panelEl || !activeEl || typeof activeEl.closest !== "function") return false;
  if (activeEl === document.body || activeEl === document.documentElement) return false;
  if (panelEl.contains(activeEl)) return false;
  const activeRow = activeEl.closest(".action-item-row");
  if (!activeRow) return false;
  const panelRow = panelEl.closest(".action-item-row");
  if (panelRow && panelRow === activeRow) return false;
  return true;
};

const sidebarArchiveButtonClass = (variant) =>
  `action-item-${variant}-btn action-item-row-action-btn--icon`;

const MetaIconCategory = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path d="M4 6h16M4 12h10M4 18h6" strokeLinecap="round" />
  </svg>
);

const MetaIconDue = () => (
  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="1" y="2.5" width="12" height="10.5" rx="1.5" />
    <path d="M4 1v3M10 1v3M1 6h12" strokeLinecap="round" />
  </svg>
);

const MetaIconMeeting = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M7 3v4M17 3v4M3.5 9.5h17M12 13v4M10 15h4" strokeLinecap="round" />
  </svg>
);

const MetaIconSubject = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path d="M4 7h16M4 12h12M4 17h8" strokeLinecap="round" />
  </svg>
);

const MetaIconAssignee = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" strokeLinecap="round" />
  </svg>
);

const MetaIconSourceDoc = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6M8 13h8M8 17h5" strokeLinecap="round" />
  </svg>
);

/**
 * One metadata row in the details panel (icon, label, value).
 *
 * @example
 * <DetailMetaRow icon={<MetaIconDue />} label="Due date" value={<ActionItemDueDateControl ... />} />
 */
const DetailMetaRow = ({ icon, label, value, iconClassName = "" }) => (
  <div className="action-item-details-sidebar__meta-row">
    <span
      className={["action-item-details-sidebar__meta-icon", iconClassName]
        .filter(Boolean)
        .join(" ")}
    >
      {icon}
    </span>
    <span className="action-item-details-sidebar__meta-label">{label}</span>
    <div className="action-item-details-sidebar__meta-value">{value}</div>
  </div>
);

/**
 * Source value: brand-colored provider name, optional identity subtext, optional filter shortcut.
 *
 * @example
 * <DetailPanelSourceValue label="Granola - himanshu@starmesh.ai" tone="granola" onFilter={fn} />
 */
const DetailPanelSourceValue = ({ label, tone = "", onFilter = null }) => {
  const { main, sub } = splitActionItemsSourceLabel(label);
  const toneModifier = actionItemSourceToneModifier(tone);
  const textClassName = [
    "action-item-details-sidebar__meta-text",
    toneModifier ? `action-item-details-sidebar__meta-text--${toneModifier}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const value = (
    <span className={textClassName}>
      {main}
      {sub ? (
        <>
          <span className="action-item-details-sidebar__meta-text-sep"> - </span>
          <span className="action-item-details-sidebar__meta-text-sub">{sub}</span>
        </>
      ) : null}
    </span>
  );
  if (!onFilter) return value;
  return (
    <div className="ds-sync-history__fetched-item-row action-item-details-sidebar__meta-title-row">
      {value}
      <button type="button" className="ds-sync-history__filter-btn" onClick={onFilter}>
        Filter by this source
      </button>
    </div>
  );
};

/**
 * Title value for the details panel — matches sync history: truncated title
 * expands on click.
 *
 * @example
 * <DetailPanelTitleValue text="Board prep with Acme" />
 *
 * @example
 * <DetailPanelTitleValue text="Weekly standup" href="https://notes.granola.ai/d/..." />
 */
const DetailPanelTitleValue = ({ text, href = null, suffix = null }) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef(null);
  const label = String(text || "").trim();
  const displayLabel = label || (href ? "View in Granola" : "");
  const isLink = Boolean(href);
  const isInteractive = !isLink && (isTruncated || expanded);

  useEffect(() => {
    setExpanded(false);
  }, [displayLabel]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el || expanded) return undefined;

    const syncTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    syncTruncation();
    const observer = new ResizeObserver(syncTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [displayLabel, expanded]);

  const toggleExpanded = () => {
    if (!isInteractive) return;
    setExpanded((value) => !value);
  };

  if (!displayLabel) {
    if (suffix) {
      return (
        <div className="ds-sync-history__fetched-item-row action-item-details-sidebar__meta-title-row">
          {suffix}
        </div>
      );
    }
    return <span className="action-item-details-sidebar__meta-text">{text}</span>;
  }

  const titleClassName = `ds-sync-history__source-title${
    expanded ? " ds-sync-history__source-title--expanded" : ""
  }${isInteractive ? " ds-sync-history__source-title--expandable" : ""}${
    isLink ? " ds-sync-history__source-title--link" : ""
  }`;

  const titleNode = isLink ? (
    <a
      ref={titleRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={titleClassName}
      title={isTruncated ? displayLabel : undefined}
    >
      {displayLabel}
    </a>
  ) : (
    <span
      ref={titleRef}
      className={titleClassName}
      onClick={isInteractive ? toggleExpanded : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleExpanded();
              }
            }
          : undefined
      }
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      title={!expanded && isTruncated ? displayLabel : undefined}
    >
      {displayLabel}
    </span>
  );

  return (
    <div
      className={`ds-sync-history__fetched-item-row action-item-details-sidebar__meta-title-row${
        expanded ? " action-item-details-sidebar__meta-title-row--expanded" : ""
      }`}
    >
      {titleNode}
      {suffix}
    </div>
  );
};

/**
 * Resolve footer "Created …" label from item timestamps.
 *
 * @example
 * resolveActionItemCreatedFooterLabel({ created_at: "2026-05-28T08:14:00Z" });
 * // "Created Wed, May 28, 2026"
 */
const resolveActionItemCreatedFooterLabel = (item) => {
  const isManual = isManualActionItem(item);
  const createdMs = parseActionItemCreatedAtMs(item);
  const meetingMs = parseMeetingLabel(String(item.meeting_from_label || "").trim());
  const footerMs = createdMs || (!isManual ? meetingMs : 0);
  const createdLabel = footerMs ? formatCreatedOnDate(footerMs) : "";
  return createdLabel ? `Created ${createdLabel}` : "";
};

/**
 * Source line for the details panel (excludes created date; that lives in the footer).
 *
 * @example
 * resolveActionItemSourceMetaLabel(
 *   { workflow_name: "Gmail - user@co.com", meeting_from_label: "28-05-2026 1:14 PM" },
 *   buildActionItemSourceProvenanceParts(item)
 * );
 * // "Gmail - user@co.com"
 */
const resolveActionItemSourceMetaLabel = (item, sourceProvenanceParts) => {
  const workflowName = String(item.workflow_name || "").trim();
  if (isManualActionItem(item)) {
    return sourceProvenanceParts?.fromClause || "Added by you";
  }
  if (isCaptureWorkflowItem(item)) {
    return CAPTURE_WORKFLOW_NAME;
  }
  const parts = [];
  if (workflowName) {
    parts.push(workflowName);
  } else if (sourceProvenanceParts?.fromClause) {
    parts.push(sourceProvenanceParts.fromClause);
  }
  return parts.join(" · ");
};

/**
 * Right-hand panel for a selected action item (To Do–style sections).
 *
 * @example
 * <ActionItemDetailsSidebar item={item} onClose={close} onTitleChange={saveTitle} onNoteChange={saveNote} onDueDateChange={setDue} />
 */
const ActionItemDetailsSidebar = ({
  item,
  workflowTypeById,
  aiResult,
  crmSideEffectResult = null,
  aiProgress,
  gmailAuthUrl,
  gmailPerformerReady,
  onGmailPerformerReady,
  onOpenGmailDraft,
  onRetryAi,
  onAiResultPatch,
  retryLoading,
  retryError = "",
  onDismissRetryError = null,
  performerBulkRunning = false,
  onClose,
  onFilterByMeeting,
  onFilterBySource,
  onViewSource,
  onHideSource,
  sourceMessages,
  sourceMessagesLoading,
  activeMessageId,
  activeSourceSnippet,
  contentCache,
  onToggleMessage,
  showSourceMessages,
  onTitleChange,
  onNoteChange,
  onDueDateChange,
  onCategoryChange,
  categoryOptions = [],
  onStatusChange,
  onDeleteItem,
  showAiAssist = true,
  updating = false,
  className = "",
  showSourceMetaRow = true,
  showOriginalSourceSection = true,
  showCreatedFooter = true,
  showFooterActions = true,
  footerContent = null,
  aiIconOnly = false,
  performerConfig = null,
  onOpenAiSettings,
  calendarPrefillUrl = null,
  serverCalendarEventUrl = null,
}) => {
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleInputRef = useRef(null);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const noteInputRef = useRef(null);

  const itemStableId = getItemStableId(item);
  // A manually added item's stable id changes once the server assigns its real key/file_id
  // (see handleAddGlobalActionItem's reconciliation). getItemSidebarKey stays constant across
  // that swap, so the note-edit reset below only fires when the user actually switches items.
  const itemSidebarKey = getItemSidebarKey(item);
  const { displayTitle, fullActionText, hasExpandableFullTask } = resolveActionItemTexts(item);
  const noteText = String(fullActionText || "").trim();
  // saveNoteEdit() leaves noteDraft set after blur so Try Again can read the
  // edit immediately. Keep showing that draft too — otherwise the Note field
  // snaps back to the previous item.action_item for a few renders (or for the
  // whole AI run if a stale list refresh lands before the PATCH is visible).
  const pendingNoteText = String(noteDraft || "").trim();
  const visibleNoteText = pendingNoteText || noteText;
  const hasNoteContent =
    Boolean(visibleNoteText) && (visibleNoteText !== displayTitle || hasExpandableFullTask);
  const isDone = item.status === STATUS_DONE;
  const isIgnored = item.status === STATUS_IGNORE;

  useEffect(() => {
    setIsTitleEditing(false);
    setTitleDraft("");
    setIsNoteEditing(false);
    setNoteDraft("");
  }, [itemSidebarKey]);

  useEffect(() => {
    if (!isTitleEditing || !titleInputRef.current) return;
    const el = titleInputRef.current;
    const syncTitleInputHeight = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    syncTitleInputHeight();
    el.addEventListener("input", syncTitleInputHeight);
    el.focus();
    try {
      el.select();
    } catch (_) {
      // Best-effort select-all for browsers that support it on textarea.
    }
    return () => el.removeEventListener("input", syncTitleInputHeight);
  }, [isTitleEditing]);

  const startTitleEdit = () => {
    if (!onTitleChange || updating) return;
    setTitleDraft(displayTitle);
    setIsTitleEditing(true);
  };

  const cancelTitleEdit = () => {
    setIsTitleEditing(false);
    setTitleDraft("");
  };

  /**
   * Persist the header title when it changed; no-op on empty or unchanged text.
   *
   * @example
   * // titleDraft = "  Follow up with Priya  ", displayTitle = "Send the deck"
   * saveTitleEdit(); // onTitleChange(item, "Follow up with Priya")
   *
   * @example
   * // titleDraft = "Send the deck" (unchanged) or "   "
   * saveTitleEdit(); // cancels edit, onTitleChange is not called
   */
  const saveTitleEdit = () => {
    const trimmed = String(titleDraft || "").trim();
    if (!trimmed || trimmed === displayTitle) {
      cancelTitleEdit();
      return;
    }
    setIsTitleEditing(false);
    onTitleChange(item, trimmed);
  };

  const handleTitleEditKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelTitleEdit();
    }
    if (event.key === "Enter") {
      event.preventDefault();
      saveTitleEdit();
    }
  };

  useEffect(() => {
    if (!isNoteEditing || !noteInputRef.current) return;
    const el = noteInputRef.current;
    const syncNoteInputHeight = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    syncNoteInputHeight();
    el.addEventListener("input", syncNoteInputHeight);
    el.focus();
    const valueLength = el.value.length;
    try {
      el.setSelectionRange(valueLength, valueLength);
    } catch (_) {
      // Best-effort cursor placement for browsers that support selection on textarea.
    }
    return () => el.removeEventListener("input", syncNoteInputHeight);
  }, [isNoteEditing]);

  const startNoteEdit = () => {
    if (!onNoteChange || updating) return;
    setNoteDraft(hasNoteContent ? visibleNoteText : "");
    setIsNoteEditing(true);
  };

  const cancelNoteEdit = () => {
    setIsNoteEditing(false);
    setNoteDraft("");
  };

  const saveNoteEdit = () => {
    const trimmed = String(noteDraft || "").trim();
    if (!trimmed) {
      cancelNoteEdit();
      return;
    }
    if (trimmed === noteText) {
      cancelNoteEdit();
      return;
    }
    setIsNoteEditing(false);
    onNoteChange(item, trimmed);
  };

  const handleNoteEditKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelNoteEdit();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      saveNoteEdit();
    }
  };

  // Not gated on isNoteEditing: saveNoteEdit() flips that to false synchronously
  // on blur, before its async onNoteChange() save resolves, so a "Try again"
  // click that follows immediately (e.g. clicking the retry button blurs this
  // field first) would otherwise read the stale noteText for a render or two.
  // noteDraft holds the latest edit either way -- unset only by cancelNoteEdit
  // (Escape, or never having edited) -- so it's the correct source regardless
  // of whether the save has landed yet.
  const revisionNotes = String(noteDraft || noteText || "").trim();
  const sourceProvenanceParts = buildActionItemSourceProvenanceParts(item, { workflowTypeById });
  const sourceMetaLabel = resolveActionItemSourceMetaLabel(item, sourceProvenanceParts);
  const sourceType = resolveActionItemConnectorSourceType(item, { workflowTypeById });
  const sourceToneModifier = actionItemSourceToneModifier(sourceType);
  const sourceFilterId = resolveActionItemSourceFilterId(item);
  const showSourceFilter = Boolean(sourceMetaLabel && onFilterBySource && sourceFilterId);
  const handleSourceFilter = showSourceFilter ? () => onFilterBySource(item) : null;
  const subjectLabel = resolveActionItemMeetingSubject(item);
  const meetingDateTimeLabel = formatMeetingLabelForDisplay(String(item.meeting_from_label || "").trim());
  const showMeetingFilter = Boolean(meetingDateTimeLabel && onFilterByMeeting);
  const handleMeetingFilter = showMeetingFilter ? () => onFilterByMeeting(item) : null;
  const categoryLabel = getActionItemCategory(item);
  const categoryPickerOptions = buildActionItemCategoryPickerOptions(
    categoryOptions,
    categoryLabel
  );
  const createdFooterLabel = resolveActionItemCreatedFooterLabel(item);
  const pageUrl = resolveActionItemPageUrl(item);
  const granolaChatUrl = resolveGranolaChatUrl(item, { workflowTypeById });
  const notionPageUrl = resolveNotionPageUrl(item, { workflowTypeById });
  const assigneeLabel = resolveActionItemAssigneeLabel(item);
  const hasSource =
    Boolean(String(item.workflow_id || "").trim() && String(item.file_id || "").trim()) ||
    (isManualActionItem(item) && (pageUrl || String(item.source_snippet || "").trim())) ||
    Boolean(String(item.source_gcs_url || "").trim());

  const transcriptOpen = Boolean(showSourceMessages && showOriginalSourceSection && hasSource);
  const readSourceLabel = resolveReadSourceLabel(sourceType);

  const aiPanelRef = useRef(null);
  const prevAiWorkingRef = useRef(false);

  const keepAiPanelFocus = useCallback(() => {
    const panel = aiPanelRef.current;
    if (!panel || typeof panel.focus !== "function") return;
    panel.focus({ preventScroll: true });
  }, []);

  // Try again / refresh on an existing park or draft must re-run, never bounce to settings.
  const handleAiRetry = useCallback(
    (opts) => {
      keepAiPanelFocus();
      const fromPanel =
        opts && typeof opts === "object" && !opts.nativeEvent ? opts : {};
      onRetryAi?.({
        ...fromPanel,
        revisionNotes: String(fromPanel.revisionNotes || revisionNotes || "").trim(),
      });
    },
    [keepAiPanelFocus, onRetryAi, revisionNotes]
  );

  const { guardedRun, gateModal } = useConnectorRunGate();

  const handleAiRunClick = useCallback(
    (opts) => {
      if (!onRetryAi) return;
      keepAiPanelFocus();
      guardedRun(onRetryAi, opts, { aiAssistType: item?.ai_assist_type });
    },
    [guardedRun, keepAiPanelFocus, onRetryAi, item?.ai_assist_type]
  );

  const isWorking =
    Boolean(retryLoading) || (performerBulkRunning && !performerResultHasVisibleBadge(aiResult));
  useLayoutEffect(() => {
    const wasWorking = prevAiWorkingRef.current;
    prevAiWorkingRef.current = isWorking;
    if (!wasWorking && !isWorking) return;
    if (wasWorking && isWorking) return;
    const panel = aiPanelRef.current;
    if (!didAiPanelFocusJumpToAnotherRow(panel, document.activeElement)) return;
    panel.focus({ preventScroll: true });
  }, [isWorking]);
  const steps = aiProgress?.steps || [];
  const itemAiAssistType = String(item?.ai_assist_type || "").trim();
  // Tasks the AI can't act on get no control and no "not supported" notice at all.
  const itemNotPerformable = !isAiActionSupported(item);
  const activeAiResult = aiResult;
  const aiPanelKey =
    activeAiResult?.type === "crm"
      ? `${itemStableId}:${buildCrmProposalKey(activeAiResult?.crm_proposed_payload)}`
      : itemStableId;
  const showHeaderAiRun =
    showAiAssist && Boolean(onRetryAi) && !isWorking && !itemNotPerformable && !performerResultHasVisibleBadge(aiResult);
  // A parked / failed notice can be closed; it comes back on the next run (new detail text).
  const aiNoticeKey = `${aiPanelKey}:${activeAiResult?.outcome || ""}:${activeAiResult?.outcome_detail || ""}`;
  const [dismissedAiNoticeKey, setDismissedAiNoticeKey] = useState("");
  const aiNoticeDismissed = dismissedAiNoticeKey === aiNoticeKey;
  const isAiNoticeOutcome =
    activeAiResult?.outcome === "parked" ||
    activeAiResult?.outcome === "pass_through" ||
    activeAiResult?.outcome === "error";
  const hasAiExpandedContent =
    showAiAssist &&
    (isWorking ||
      (isAiNoticeOutcome && !aiNoticeDismissed) ||
      Boolean(performerResultHasExpandedPanel(activeAiResult)));
  useEffect(() => {
    if (isWorking) setDismissedAiNoticeKey("");
  }, [isWorking]);

  const showArchiveFooterActions =
    showFooterActions && (Boolean(onStatusChange) || Boolean(onDeleteItem));
  const showFooter =
    Boolean(footerContent) ||
    (showCreatedFooter && Boolean(createdFooterLabel)) ||
    showArchiveFooterActions;

  const sidebarClassName = ["action-item-details-sidebar", className]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={sidebarClassName}>
      <div className="action-item-details-sidebar__header">
        {onStatusChange ? (
          <button
            type="button"
            className={`action-item-check-btn action-item-details-sidebar__check${isDone ? " active" : ""}`}
            onClick={() => onStatusChange(item, isDone ? "pending" : STATUS_DONE)}
            disabled={updating}
            title={isDone ? "Mark incomplete" : "Mark complete"}
            aria-label={isDone ? "Mark incomplete" : "Mark complete"}
          />
        ) : null}
        <h2 className="action-item-details-sidebar__title">
          {onTitleChange && isTitleEditing ? (
            <textarea
              ref={titleInputRef}
              className="action-item-details-sidebar__title-edit-input"
              value={titleDraft}
              disabled={updating}
              rows={1}
              aria-label="Task title"
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={handleTitleEditKeyDown}
              onBlur={saveTitleEdit}
            />
          ) : onTitleChange ? (
            <button
              type="button"
              className="action-item-details-sidebar__title-trigger"
              disabled={updating}
              title="Click to edit title"
              aria-label="Edit task title"
              onClick={startTitleEdit}
            >
              {displayTitle}
            </button>
          ) : (
            displayTitle
          )}
        </h2>
        {showHeaderAiRun ? (
          <div className="action-item-details-sidebar__ai">
            <AIRunButton
              iconOnly
              onClick={handleAiRunClick}
              loading={retryLoading}
              hasResult={Boolean(aiResult)}
            />
          </div>
        ) : null}
        <button
          type="button"
          className="action-item-details-sidebar__close"
          onClick={onClose}
          aria-label="Close details"
        >
          ✕
        </button>
      </div>
      <RunErrorNotice message={retryError} onDismiss={onDismissRetryError} variant="banner" />

      <div className="action-item-details-sidebar__scroll">
        <section className="action-item-details-sidebar__panel-section">
          {showSourceMetaRow && sourceMetaLabel ? (
            <DetailMetaRow
              icon={renderActionItemSourceIcon(item, { workflowTypeById })}
              iconClassName={[
                "action-item-details-sidebar__meta-icon--source",
                sourceToneModifier
                  ? `action-item-details-sidebar__meta-icon--${sourceToneModifier}`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              label="From"
              value={
                <DetailPanelSourceValue
                  label={sourceMetaLabel}
                  tone={sourceType}
                  onFilter={handleSourceFilter}
                />
              }
            />
          ) : null}
          {subjectLabel || granolaChatUrl || notionPageUrl ? (
            <DetailMetaRow
              icon={<MetaIconSubject />}
              label={notionPageUrl ? "Page" : "Title"}
              value={
                <DetailPanelTitleValue
                  text={subjectLabel || (notionPageUrl ? "Open in Notion" : "")}
                  href={granolaChatUrl || notionPageUrl || null}
                />
              }
            />
          ) : null}
          {assigneeLabel ? (
            <DetailMetaRow
              icon={<MetaIconAssignee />}
              label="Assigned to"
              value={<span className="action-item-details-sidebar__meta-text">{assigneeLabel}</span>}
            />
          ) : null}
          <DetailMetaRow
            icon={<MetaIconCategory />}
            label="Category"
            value={
              onCategoryChange ? (
                <ActionItemCategoryControl
                  value={categoryLabel}
                  options={categoryPickerOptions}
                  disabled={updating}
                  onChange={(label) => onCategoryChange(item, label)}
                />
              ) : (
                <span className="action-item-details-sidebar__meta-text">{categoryLabel}</span>
              )
            }
          />
          {meetingDateTimeLabel ? (
            <DetailMetaRow
              icon={<MetaIconMeeting />}
              label="Meeting time"
              value={
                showMeetingFilter ? (
                  <div className="ds-sync-history__fetched-item-row action-item-details-sidebar__meta-title-row">
                    <span className="action-item-details-sidebar__meta-text">{meetingDateTimeLabel}</span>
                    <button
                      type="button"
                      className="ds-sync-history__filter-btn"
                      onClick={handleMeetingFilter}
                    >
                      Filter by this meeting
                    </button>
                  </div>
                ) : (
                  <span className="action-item-details-sidebar__meta-text">{meetingDateTimeLabel}</span>
                )
              }
            />
          ) : null}
          {onDueDateChange ? (
            <DetailMetaRow
              icon={<MetaIconDue />}
              label="Due date"
              value={
                <ActionItemDueDateControl
                  value={item.due_date}
                  disabled={updating}
                  showEmptyHint
                  onChange={(iso) => onDueDateChange(item, iso)}
                />
              }
            />
          ) : null}
        </section>

        {hasAiExpandedContent ? (
          <section
            ref={aiPanelRef}
            className="action-item-details-sidebar__panel-section action-item-details-sidebar__panel-section--ai"
            tabIndex={-1}
          >
            <div className="action-item-details-sidebar__ai-heading-row">
              <h3 className="action-item-details-sidebar__section-heading action-item-details-sidebar__section-heading--ai">AI Actions</h3>
              {SHOW_AI_ASSIST_COST && typeof activeAiResult?.cost_usd === "number" && activeAiResult.cost_usd > 0 ? (
                <span className="action-item-details-sidebar__ai-cost-badge">
                  ${activeAiResult.cost_usd.toFixed(3)}
                </span>
              ) : null}
            </div>
            {isWorking ? (
              <AIProgressExpanded
                key={aiPanelKey}
                steps={steps}
                aiAssistType={itemAiAssistType}
              />
            ) : null}
            {!isWorking && isAiNoticeOutcome && !aiNoticeDismissed ? (
              <AIParkExpanded
                key={aiPanelKey}
                iconOnly={aiIconOnly}
                result={activeAiResult}
                onRetry={handleAiRetry}
                onDismiss={() => setDismissedAiNoticeKey(aiNoticeKey)}
                retryLoading={retryLoading}
              />
            ) : null}
            {!isWorking && performerResultHasExpandedPanel(activeAiResult) ? (
              <AIDraftExpanded
                key={aiPanelKey}
                iconOnly={aiIconOnly}
                result={activeAiResult}
                crmSideEffectResult={crmSideEffectResult}
                gmailAuthUrl={gmailAuthUrl}
                gmailPerformerReady={gmailPerformerReady}
                onGmailPerformerReady={onGmailPerformerReady}
                onOpenGmailDraft={onOpenGmailDraft}
                onRetry={handleAiRetry}
                onResultPatch={onAiResultPatch}
                retryLoading={retryLoading}
                revisionNotes={revisionNotes}
                gmailThreadId={String(item?.thread_id || "").trim()}
                sourceSnippet={String(item?.source_snippet || "").trim()}
                calendarPrefillUrl={calendarPrefillUrl}
                serverCalendarEventUrl={serverCalendarEventUrl}
              />
            ) : null}
          </section>
        ) : null}

        {onNoteChange ? (
          <>
            <section className="action-item-details-sidebar__panel-section">
              <h3 className="action-item-details-sidebar__section-heading">Source</h3>
              {isNoteEditing ? (
                <div className="action-item-details-sidebar__note-edit-wrap">
                  <textarea
                    ref={noteInputRef}
                    className="action-item-details-sidebar__note-edit-input"
                    value={noteDraft}
                    disabled={updating}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={handleNoteEditKeyDown}
                    onBlur={saveNoteEdit}
                    rows={1}
                    placeholder="Add note"
                    aria-label="Edit note"
                  />
                  <span className="action-item-details-sidebar__note-hint">
                    Ctrl/Cmd+Enter to save, Escape to discard, or click away to save
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className={`action-item-details-sidebar__note-trigger${
                    hasNoteContent ? " action-item-details-sidebar__note-trigger--filled" : ""
                  }`}
                  disabled={updating}
                  onClick={startNoteEdit}
                >
                  {hasNoteContent ? visibleNoteText : "Add note"}
                </button>
              )}
            </section>
          </>
        ) : null}

        {showOriginalSourceSection && hasSource ? (
          <section
            className={`action-item-details-sidebar__panel-section${
              transcriptOpen ? " action-item-details-sidebar__panel-section--reading" : ""
            }`}
          >
            <button
              type="button"
              className={`action-item-details-sidebar__read-source-btn${
                transcriptOpen ? " action-item-details-sidebar__read-source-btn--open" : ""
              }`}
              onClick={transcriptOpen ? onHideSource : onViewSource}
              aria-expanded={transcriptOpen}
              title={transcriptOpen ? `Hide ${readSourceLabel.replace(/^Read /, "")}` : readSourceLabel}
            >
              <MetaIconSourceDoc />
              <span>{transcriptOpen ? `Hide ${readSourceLabel.replace(/^Read /, "")}` : readSourceLabel}</span>
            </button>
            {transcriptOpen ? (
              <div className="action-item-details-sidebar__source-list action-item-details-sidebar__source-list--inline">
                {sourceMessagesLoading ? <InlineSpinner text="Loading…" /> : null}
                {!sourceMessagesLoading && sourceMessages.length === 0 ? (
                  <p className="action-item-details-sidebar__empty">No sources found for this item.</p>
                ) : null}
                {sourceMessages.map((msg) => {
                  const id = String(msg.message_id || "");
                  const isOnlyMessage = sourceMessages.length === 1;
                  return (
                    <SourceMessageAccordion
                      key={id}
                      variant={isOnlyMessage ? "transcript" : "default"}
                      sourceMessage={{
                        ...msg,
                        source_snippet: id === activeMessageId ? activeSourceSnippet : "",
                      }}
                      isExpanded={id === activeMessageId}
                      cacheEntry={contentCache[id]}
                      onToggle={onToggleMessage}
                      timeLabel={meetingDateTimeLabel}
                    />
                  );
                })}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      {showFooter ? (
        <footer className="action-item-details-sidebar__footer">
          {footerContent ? (
            <div className="action-item-details-sidebar__footer-custom">{footerContent}</div>
          ) : null}
          {showCreatedFooter && createdFooterLabel ? (
            <span className="action-item-details-sidebar__created">{createdFooterLabel}</span>
          ) : !footerContent ? (
            <span className="action-item-details-sidebar__created" aria-hidden="true" />
          ) : null}
          {showArchiveFooterActions ? (
            <div className="action-item-details-sidebar__footer-actions">
              {isIgnored && onDeleteItem ? (
                <>
                  {onStatusChange ? (
                    <button
                      type="button"
                      className={sidebarArchiveButtonClass("restore")}
                      onClick={() => onStatusChange(item, "pending")}
                      disabled={updating}
                      title="Restore to open tasks"
                      aria-label="Restore"
                    >
                      <ActionItemRestoreIcon size={16} />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={sidebarArchiveButtonClass("delete")}
                    onClick={() => onDeleteItem(item)}
                    disabled={updating}
                    title="Delete permanently"
                    aria-label="Delete permanently"
                  >
                    <ActionItemDeleteIcon size={16} />
                  </button>
                </>
              ) : onStatusChange ? (
                <button
                  type="button"
                  className={sidebarArchiveButtonClass("archive")}
                  onClick={() => onStatusChange(item, STATUS_IGNORE)}
                  disabled={updating}
                  title="Archive — remove without completing"
                  aria-label="Archive"
                >
                  <ActionItemArchiveIcon size={16} />
                </button>
              ) : null}
            </div>
          ) : null}
        </footer>
      ) : null}
      {gateModal}
    </aside>
  );
};

export default ActionItemDetailsSidebar;
